// MACHINERY V2 (Stroje) — "Vlastný stroj", the type-gated custom-machine form
// (M2.7).
//
// Spec: data package §10. Reference impl:
// design_handoff_machinery_v3/machinery-catalog.jsx `CustomMachineDialog`
// (lines 202-300). Artboards 0802 (type not yet chosen) / 0803 (CNC) / 0804
// (Linka). Form behaviour is useCustomMachineForm.ts; the field sets are
// CustomMachineFields.tsx + CustomMachineTail.tsx (300-line cap).
//
// >>> THE TYPE GATE IS A <fieldset disabled>, NOT AN OPACITY TRICK. <<<
// The designer wraps everything below the type select in
// `opacity: stage === "type-open" ? 0.4 : 1` (machinery-catalog.jsx:237), and
// artboard 0802 shows the submit button dimmed to match (:216). Opacity alone would
// leave the Lokácia control clickable and tab-reachable while it looks
// unavailable, so the dim is carried by a native disabled fieldset: same 0.4,
// plus the browser's own "nothing in here is operable" semantics for pointer,
// keyboard and assistive tech alike.
// NOTE WHAT 0802 DOES *NOT* SHOW: no CNC or Linka field. The designer's own
// `isCnc`/`isLine` guards render neither set until a type is chosen, so the
// gated region holds exactly ONE field — Lokácia — until then, and the dialog
// is short. That is why this file does not pick a "default" field set to dim.
//
// >>> THE DIALOG STAYS OPEN UNTIL createMachine CONFIRMS (§9 D7). <<<
// `submitError` renders a RetryNotice directly above the footer, beside the
// button that failed, and NOTHING is cleared — every typed value, the location
// and the whole image queue survive, because the hook never closes on failure.
// See useCustomMachineForm.ts's header for the v1 behaviour this replaces.
//
// SHELL = profile/tabs/TabDialog.tsx, the shipped v2 dialog. It already carries
// the portal rule (scope classes re-carried, transparent background, palette
// read LIVE) and the stopPropagation guard on BOTH portalled siblings; a second
// shell would be a second place to get those right. Its only new prop is
// `subtitle`, which is where the back link goes.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useCallback, useMemo, useRef, useState } from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import RetryNotice from "../feedback/RetryNotice";
import FormField from "../forms/FormField";
import { ImageGlyph, MachineTypeGlyph, WrenchGlyph } from "../forms/FormGlyphs";
import TabDialog from "../profile/tabs/TabDialog";
import { BackLink } from "./addAtoms";
import { SelectRow } from "./customMachineFieldAtoms";
import CustomMachineFields from "./CustomMachineFields";
import MachineImagesField from "./MachineImagesField";
import {
  axisConfigurationOptions,
  controlSystemModelNames,
  isProductionLine,
  locationOptions,
  machineTypeOptions,
  referenceOptions,
} from "./customMachineOptions";
import { acceptImages, mergeImages, removeImage, setPrimaryImage } from "./machineImages";
import { useCustomMachineForm } from "./useCustomMachineForm";

/** The footer button submits the body's form across the DOM boundary TabDialog
 *  puts between them — a plain `form=` association, no ref plumbing. */
const FORM_ID = "mv2-custom-machine-form";

export interface CustomMachineDialogProps {
  open: boolean;
  /** Close the whole add flow (X / esc / backdrop / Zrušiť). */
  onClose: () => void;
  /** "Späť na možnosti" — back to AddMachinePicker, form discarded. */
  onBack: () => void;
  /** Fired ONLY after createMachine resolves. Closes and refreshes the fleet. */
  onCreated: (id?: string) => void;
}

const CustomMachineDialog: React.FC<CustomMachineDialogProps> = ({
  open,
  onClose,
  onBack,
  onCreated,
}) => {
  const { translate } = useTranslation(["myMachinery", "common"]);
  const [rejections, setRejections] = useState<string[]>([]);

  const f = useCustomMachineForm(open, onCreated);
  const {
    form,
    types,
    manufacturers,
    operationTypes,
    axisConfigurations,
    controlSystemBrands,
    controlSystemModels,
    automationLevels,
    locations,
    typeId,
    manufacturerId,
    controlSystemBrandId,
    images,
    imagesRef,
    setImages,
    submitting,
    submitError,
    resetAll,
    submit,
  } = f;

  const typeChosen = !!typeId;
  const isLine = isProductionLine(types, typeId);

  /** §10: the "Other" manufacturer unlocks customBrandName — CNC form ONLY. */
  const showCustomBrand =
    !isLine && manufacturers.find((m) => m.id === manufacturerId)?.name === "Other";

  const typeOpts = useMemo(() => machineTypeOptions(types, translate), [types, translate]);
  const locationOpts = useMemo(
    () => locationOptions(locations, translate),
    [locations, translate]
  );
  const manufacturerOpts = useMemo(() => referenceOptions(manufacturers), [manufacturers]);
  const operationTypeOpts = useMemo(() => referenceOptions(operationTypes), [operationTypes]);
  const axisOpts = useMemo(() => axisConfigurationOptions(axisConfigurations), [axisConfigurations]);
  const controlBrandOpts = useMemo(
    () => referenceOptions(controlSystemBrands),
    [controlSystemBrands]
  );
  const automationOpts = useMemo(() => referenceOptions(automationLevels), [automationLevels]);
  const controlModelNames = useMemo(
    () => controlSystemModelNames(controlSystemModels),
    [controlSystemModels]
  );

  /**
   * >>> ACCEPTS ARE SERIALISED AND BOTH ENDS READ THE LIVE QUEUE. <<<
   * `acceptImages` awaits a FileReader per file (machineImages.ts:133), so a
   * second drop landing while the first is still reading used to compute
   * against the SAME render-time `images` array. Three ways that lost the
   * user's work, all fixed here: the later `setImages` REPLACED the earlier
   * batch instead of appending to it; the free-slot clamp
   * (`MAX_COUNT - current.length`, machineImages.ts:99) was computed from a
   * stale — usually 0 — length, so the 10-image cap could be exceeded with no
   * `tooManyFiles` notice; and a row REMOVED during the read was restored by
   * the stale write. The chain makes a second accept wait for the first, and
   * both the clamp input and the merge base are `imagesRef.current`, the
   * queue's authority (useCustomMachineForm.ts). D7 in miniature: this dialog
   * never discards what the user put into it.
   */
  const acceptChain = useRef<Promise<void>>(Promise.resolve());
  const addFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      acceptChain.current = acceptChain.current
        .then(async () => {
          const result = await acceptImages(imagesRef.current, files);
          setRejections(result.rejections.map((r) => translate(r.key, r.params)));
          if (result.added.length > 0) setImages(mergeImages(imagesRef.current, result.added));
        })
        // A thrown accept must not WEDGE the chain — that would kill every
        // later upload silently. The queue is untouched when it throws, and
        // every expected failure already returns as a `rejections` line.
        .catch(() => undefined);
    },
    [imagesRef, setImages, translate]
  );

  const leave = (next: () => void) => {
    resetAll();
    setRejections([]);
    next();
  };

  return (
    <TabDialog
      open={open}
      onOpenChange={(o) => {
        if (!o) leave(onClose);
      }}
      width={620}
      title={translate("myMachinery:v2.add.customTitle")}
      glyph={<WrenchGlyph />}
      subtitle={
        <BackLink label={translate("myMachinery:v2.add.backToOptions")} onClick={() => leave(onBack)} />
      }
      footerNote={translate(
        typeChosen ? "myMachinery:v2.add.requiredNote" : "myMachinery:v2.add.dismissNote"
      )}
      footer={
        <React.Fragment>
          <button type="button" className="btn btn-ghost" onClick={() => leave(onClose)}>
            {translate("common:buttons.cancel")}
          </button>
          <button
            type="submit"
            form={FORM_ID}
            className="btn btn-mod"
            disabled={!typeChosen || submitting}
          >
            <span aria-hidden style={{ fontSize: 15, lineHeight: 1, marginRight: 2 }}>
              +
            </span>
            {translate("myMachinery:v2.add.submit")}
          </button>
        </React.Fragment>
      }
    >
      <form id={FORM_ID} onSubmit={submit} noValidate style={{ minWidth: 0 }}>
        <FormField glyph={<ImageGlyph />} label={translate("myMachinery:v2.add.images")}>
          <MachineImagesField
            images={images}
            onFiles={addFiles}
            onRemove={(key) => setImages(removeImage(imagesRef.current, key))}
            onSetPrimary={(key) => setImages(setPrimaryImage(imagesRef.current, key))}
            rejections={rejections}
          />
        </FormField>

        {/* Registered through the same Controller-backed row as every other
            select, so `typeId` is in the submit payload and carries v1's own
            rule (`register("typeId", { required })`, MachineDialog.tsx:944).
            v1 also DISABLES it when editing — not reachable here, because this
            dialog only ever creates. */}
        <SelectRow
          form={form}
          name="typeId"
          label={translate("myMachinery:machineType")}
          glyph={<MachineTypeGlyph />}
          required
          placeholder={translate("myMachinery:chooseFromList")}
          options={typeOpts}
        />

        <fieldset
          disabled={!typeChosen}
          style={{
            display: "block",
            border: 0,
            padding: 0,
            margin: 0,
            minWidth: 0,
            opacity: typeChosen ? 1 : 0.4,
          }}
        >
          <CustomMachineFields
            form={form}
            typeChosen={typeChosen}
            isLine={isLine}
            showCustomBrand={showCustomBrand}
            locationId={f.locationId}
            onLocationChange={(id) => f.setLocationId(id || null)}
            locationOpts={locationOpts}
            manufacturerOpts={manufacturerOpts}
            operationTypeOpts={operationTypeOpts}
            axisOpts={axisOpts}
            controlBrandOpts={controlBrandOpts}
            automationOpts={automationOpts}
            controlModelNames={controlModelNames}
            systemDisabled={controlSystemBrandId === ""}
          />
        </fieldset>

        {submitError && (
          <div style={{ marginTop: 4 }}>
            <RetryNotice
              title={submitError}
              hint={translate("myMachinery:v2.add.createFailedHint")}
              onRetry={submit}
              busy={submitting}
            />
          </div>
        )}
      </form>
    </TabDialog>
  );
};

export default CustomMachineDialog;
